import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  Filter,
  MessageSquare,
  Eye,
  Settings,
  BarChart3,
  CheckCircle,
  XCircle
} from 'lucide-react';
import adminCompanyService from '../services/adminCompanyService';
import adminChatbotsService from '../services/adminChatbotsService';

const ChatbotsOverview = () => {
  const [chatbots, setChatbots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // View modal
  const [viewBot, setViewBot] = useState(null);

  // Edit modal
  const [editBot, setEditBot] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Analytics modal
  const [analyticsBot, setAnalyticsBot] = useState(null);
  const [analyticsPeriod, setAnalyticsPeriod] = useState('30days');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  useEffect(() => {
    adminCompanyService.getPaged(null, null, 0, 1000)
      .then(data => {
        const fetches = (data?.content || []).map(c =>
          adminChatbotsService.getByCompany(c.id)
            .then(bots => (bots || []).map(bot => ({ ...bot, companyName: c.name, companyId: c.id })))
            .catch(() => [])
        );
        return Promise.all(fetches);
      })
      .then(results => {
        setChatbots(results.flat());
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, []);

  const openEdit = (bot) => {
    setEditBot(bot);
    setSaveError(null);
    setEditForm({
      name: bot.name || '',
      description: bot.description || '',
      isActive: bot.isActive ?? true,
      personality: bot.personality || '',
      systemInstructions: bot.systemInstructions || '',
      welcomeMessage: bot.welcomeMessage || '',
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await adminChatbotsService.update(editBot.id, editBot.companyId, editForm);
      setChatbots(prev => prev.map(b =>
        b.id === updated.id ? { ...updated, companyName: b.companyName, companyId: b.companyId } : b
      ));
      setEditBot(null);
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const openAnalytics = async (bot, period = analyticsPeriod) => {
    setAnalyticsBot(bot);
    setAnalyticsData(null);
    setAnalyticsLoading(true);
    try {
      const data = await adminChatbotsService.getAnalytics(bot.companyId, period);
      setAnalyticsData(data);
    } catch (err) {
      console.error(err);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handlePeriodChange = (period) => {
    setAnalyticsPeriod(period);
    if (analyticsBot) openAnalytics(analyticsBot, period);
  };

  const getStatusBadge = (isActive) => {
    if (isActive) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
          <CheckCircle className="w-3 h-3" />
          Active
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
        <XCircle className="w-3 h-3" />
        Inactive
      </span>
    );
  };

  const filteredChatbots = chatbots.filter(bot =>
    searchTerm === '' ||
    bot.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    bot.companyName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <>
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Chatbots Overview</h1>
          <p className="text-gray-600 mt-1">
            Monitor all chatbots across the platform - {chatbots.length} total
          </p>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by chatbot name or company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div className="flex gap-3">
            <select className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent">
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2">
              <Filter className="w-4 h-4" />
              More Filters
            </button>
          </div>
        </div>
      </div>

      {/* Chatbots Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Chatbot
                </th>
                <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Widget Key
                </th>
                <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredChatbots.map((bot) => (
                <tr key={bot.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                        <MessageSquare className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{bot.name || 'Unnamed Bot'}</div>
                        <div className="text-sm text-gray-500">{bot.description || '-'}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-gray-700">
                    {bot.companyName}
                  </td>
                  <td className="px-6 py-4">
                    <code className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-700">
                      {bot.widgetKey || '-'}
                    </code>
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(bot.isActive)}
                  </td>
                  <td className="px-6 py-4 text-gray-600 text-sm">
                    {bot.createdAt ? new Date(bot.createdAt).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setViewBot(bot)}
                        className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                        title="View details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openEdit(bot)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit chatbot"
                      >
                        <Settings className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openAnalytics(bot)}
                        className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                        title="View analytics"
                      >
                        <BarChart3 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Results Count */}
        <div className="px-6 py-4 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            Showing <span className="font-medium">{filteredChatbots.length}</span> chatbots
            {searchTerm ? ` (filtered from ${chatbots.length} total)` : ''}
          </div>
        </div>
      </div>
    </div>

    {/* ── View Modal ── */}
    {viewBot && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setViewBot(null)}>
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
          <div className="flex items-start justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">{viewBot.name || 'Unnamed Bot'}</h2>
                <p className="text-sm text-gray-500">{viewBot.companyName}</p>
              </div>
            </div>
            <button onClick={() => setViewBot(null)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
              <span className="text-xl leading-none">&times;</span>
            </button>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-2">
              {getStatusBadge(viewBot.isActive)}
            </div>
            {viewBot.description && (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Description</p>
                <p className="text-sm text-gray-700">{viewBot.description}</p>
              </div>
            )}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Widget Key</p>
              <code className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-700 break-all">{viewBot.widgetKey || '—'}</code>
            </div>
            {viewBot.personality && (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Personality</p>
                <p className="text-sm text-gray-700">{viewBot.personality}</p>
              </div>
            )}
            {viewBot.welcomeMessage && (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Welcome Message</p>
                <p className="text-sm text-gray-700">{viewBot.welcomeMessage}</p>
              </div>
            )}
            {viewBot.systemInstructions && (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">System Instructions</p>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{viewBot.systemInstructions}</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-100">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Created</p>
                <p className="text-sm text-gray-700">{viewBot.createdAt ? new Date(viewBot.createdAt).toLocaleString() : '—'}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Updated</p>
                <p className="text-sm text-gray-700">{viewBot.updatedAt ? new Date(viewBot.updatedAt).toLocaleString() : '—'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* ── Edit Modal ── */}
    {editBot && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setEditBot(null)}>
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Edit Chatbot</h2>
              <p className="text-sm text-gray-500">{editBot.companyName}</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setEditBot(null)} disabled={saving}
                className="px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving}
                className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50">
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
          {saveError && (
            <div className="mx-6 mt-4 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{saveError}</div>
          )}
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Name</label>
              <input value={editForm.name}
                onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Description</label>
              <textarea value={editForm.description} rows={2}
                onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Personality</label>
              <input value={editForm.personality}
                onChange={e => setEditForm(f => ({ ...f, personality: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Welcome Message</label>
              <input value={editForm.welcomeMessage}
                onChange={e => setEditForm(f => ({ ...f, welcomeMessage: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">System Instructions</label>
              <textarea value={editForm.systemInstructions} rows={4}
                onChange={e => setEditForm(f => ({ ...f, systemInstructions: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none" />
            </div>
            <div className="flex items-center gap-3">
              <input type="checkbox" id="botIsActive" checked={editForm.isActive}
                onChange={e => setEditForm(f => ({ ...f, isActive: e.target.checked }))}
                className="w-4 h-4 text-primary-600 rounded border-gray-300" />
              <label htmlFor="botIsActive" className="text-sm text-gray-700">Chatbot is active</label>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* ── Analytics Modal ── */}
    {analyticsBot && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setAnalyticsBot(null)}>
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-purple-500" />
                Analytics
              </h2>
              <p className="text-sm text-gray-500">{analyticsBot.name} · {analyticsBot.companyName}</p>
            </div>
            <button onClick={() => setAnalyticsBot(null)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
              <span className="text-xl leading-none">&times;</span>
            </button>
          </div>
          <div className="p-6 space-y-4">
            {/* Period selector */}
            <div className="flex gap-2">
              {['7days', '30days', '90days'].map(p => (
                <button key={p}
                  onClick={() => handlePeriodChange(p)}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${analyticsPeriod === p ? 'bg-primary-600 text-white' : 'border border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                  {p === '7days' ? '7 days' : p === '30days' ? '30 days' : '90 days'}
                </button>
              ))}
            </div>

            {analyticsLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : analyticsData ? (
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: 'Total Conversations', value: analyticsData.totalConversations ?? '—' },
                  { label: 'Total Messages', value: analyticsData.totalMessages ?? '—' },
                  { label: 'Avg Messages / Conv', value: analyticsData.avgMessagesPerConversation != null ? analyticsData.avgMessagesPerConversation.toFixed(1) : '—' },
                  { label: 'Satisfaction Rate', value: analyticsData.satisfactionRate != null ? `${(analyticsData.satisfactionRate * 100).toFixed(0)}%` : '—' },
                  { label: 'Avg Response Time', value: analyticsData.avgResponseTimeMs != null ? `${(analyticsData.avgResponseTimeMs / 1000).toFixed(1)}s` : '—' },
                  { label: 'Active Users', value: analyticsData.uniqueUsers ?? '—' },
                ].map(({ label, value }) => (
                  <div key={label} className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs text-gray-500 mb-1">{label}</p>
                    <p className="text-2xl font-bold text-gray-900">{value}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-400 py-8">No analytics data available</p>
            )}
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default ChatbotsOverview;
